import { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { compareCzech, isCountryLevel, sortBySearchMatch } from "@ckgo2/shared";

interface Item {
  id: string;
  title?: string;
  name?: string;
  slug?: string;
  featuredImage?: string;
  /** Jen u destinací: hledání pak při stejné shodě řadí zemi před lokalitu. */
  parentId?: string | null;
  showAsCountry?: boolean | null;
}

/** Zobrazený název položky (zájezd a inspirace `title`, destinace `name`). */
const itemLabel = (item: Item) => item.title || item.name;
/** Česká abeceda nad zobrazeným názvem (F1.8): volby bez hledání i vybrané čipy. */
const byItemLabel = (a: Item, b: Item) => compareCzech(itemLabel(a), itemLabel(b));

interface RelatedItemsPickerProps<T extends Item> {
  items: T[];
  /**
   * Nepovinná skupina před abecedou (nižší číslo napřed), ve volbách i v čipech. Volající,
   * který chce určité položky nahoře (inspirace ze stejné země), ji předá sem: pořadí pole
   * `items` picker nebere, rozhoduje abeceda.
   */
  rank?: (item: T) => number;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  placeholder?: string;
}

export default function RelatedItemsPicker<T extends Item>({
  items,
  rank,
  selectedIds,
  onChange,
  label,
  placeholder,
}: RelatedItemsPickerProps<T>) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  /** Skupina (`rank`), pak česká abeceda názvu. */
  const order = useCallback(
    (a: T, b: T) => (rank ? rank(a) - rank(b) : 0) || byItemLabel(a, b),
    [rank],
  );

  // Hledání filtruje i řadí podle shody s názvem. Úroveň země rozhoduje jen u řádků
  // destinací (nesou klíč `parentId`): u zájezdu nebo inspirace by `isCountryLevel` vrátil
  // `true` a celé pole by bylo „země". Bez hledaného textu (a při stejné shodě) rozhoduje
  // skupina `rank` a v ní česká abeceda názvu.
  const filtered = useMemo(
    () =>
      sortBySearchMatch(items, search, {
        name: itemLabel,
        other: (item) => [item.slug],
        isCountryLevel: (item) => item.parentId !== undefined && isCountryLevel(item),
        tieBreak: order,
      }),
    [items, search, order],
  );

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.includes(item.id)).sort(order),
    [items, selectedIds, order],
  );
  const availableItems = filtered.filter((item) => !selectedIds.includes(item.id));

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label ?? t("shared.relatedItems.defaultLabel")}
      </h3>

      {/* Selected items */}
      {selectedItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {t("shared.relatedItems.selectedCount", { count: selectedItems.length })}
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item) => (
              <span
                key={item.id}
                className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
              >
                {itemLabel(item)}
                <button
                  title={t("common.actions.remove")}
                  type="button"
                  onClick={() => toggle(item.id)}
                  className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-9"
          placeholder={placeholder ?? t("shared.relatedItems.searchPlaceholder")}
        />
      </div>

      {/* Available items */}
      <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
        {availableItems.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-400">
            {search ? t("shared.relatedItems.noMatches") : t("shared.relatedItems.noItems")}
          </p>
        ) : (
          availableItems.map((item) => (
            <label
              key={item.id}
              className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <input
                type="checkbox"
                checked={false}
                onChange={() => toggle(item.id)}
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{itemLabel(item)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
